/**
 * Slash command palette — anchored under the equation block.
 *
 * Opens with `\` or `/`. Search input is mono with a non-editable
 * `\` prefix. ↑/↓ cycles results, ↵ inserts (keeps focus for chains),
 * Esc clears the query.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { buildSlashIndex, matchScore, type SlashItem } from "./palette";
import { T, type Token } from "./tokens";

interface SlashPaletteProps {
  onInsert: (token: Token) => void;
  onClose: () => void;
}

/** Expand a structure command (`\frac`, `\sqrt`, …) into its struct token. */
function structTokenFor(cmd: string): Token {
  switch (cmd) {
    case "\\frac":     return T.frac();
    case "\\sqrt":     return T.sqrt();
    case "\\sqrt[n]":  return T.nthroot();
    case "\\int":      return T.integral();
    case "\\sum":      return T.sumStruct();
    case "\\lim":      return T.limStruct();
    case "\\binom":    return T.binom();
    case "\\matrix":
    case "\\pmatrix":
    case "\\bmatrix":  return T.matrix(2, 2);
    case "\\cases":    return T.cases();
    case "\\aligned":  return T.aligned();
    case "\\colvec":   return T.vector();
    case "\\vec":      return T.accent("vec");
    case "\\hat":      return T.accent("hat");
    case "\\bar":      return T.accent("bar");
    case "\\tilde":    return T.accent("tilde");
    case "\\overline": return T.accent("overline");
    case "\\dot":      return T.accent("dot");
    case "\\abs":      return T.abs();
    default:           return T.letter(cmd.replace(/^\\/, ""));
  }
}

function tokenForItem(item: SlashItem): Token {
  const { cat, entry } = item;
  switch (cat) {
    case "struct": return structTokenFor(entry.c);
    case "greek":  return /^[αβγδεζηθικλμνξπρστφχψω]$/.test(entry.g) ? T.greek(entry.g) : T.upletter(entry.g);
    case "ops":    return T.op(entry.g);
    case "rel":    return T.rel(entry.g);
    case "bigops": return T.bigop(entry.g);
    case "arrows": return T.arrow(entry.g);
    case "fn":     return T.fn(entry.g);
    case "logic":  return T.op(entry.g);
    case "sets":   return T.upletter(entry.g);
    default:       return T.letter(entry.g);
  }
}

export function SlashPalette({ onInsert, onClose }: SlashPaletteProps) {
  const all = useMemo(() => buildSlashIndex(), []);
  const [query, setQuery] = useState("");
  const [selIdx, setSelIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo(() => {
    if (!query) return all.slice(0, 80);
    const q = query.toLowerCase().replace(/^[\\/]/, "");
    return all
      .map((item) => ({ item, score: matchScore(item, q) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 80)
      .map((r) => r.item);
  }, [query, all]);

  useEffect(() => {
    setSelIdx(0);
  }, [query]);

  // Keep the selected row visible. Defensively check for the method
  // — jsdom (used in unit tests) doesn't ship it, and we don't want
  // a crash to take down the popover render.
  useEffect(() => {
    const el = listRef.current?.children[selIdx] as HTMLElement | undefined;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [selIdx]);

  function commit(item: SlashItem) {
    onInsert(tokenForItem(item));
    setQuery("");
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      if (query) {
        setQuery("");
      } else {
        onClose();
      }
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdx((i) => Math.min(results.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = results[selIdx];
      if (item) commit(item);
      return;
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        bottom: -8,
        transform: "translate(-50%, 100%)",
        width: 360,
        maxHeight: 360,
        background: "var(--lilia-surface)",
        border: "1px solid var(--lilia-border)",
        borderRadius: 8,
        boxShadow: "0 18px 38px rgba(0,0,0,0.14), 0 3px 8px rgba(0,0,0,0.05)",
        display: "flex", flexDirection: "column",
        minHeight: 0,
        zIndex: 30,
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 10px",
        borderBottom: "1px solid var(--lilia-border)",
        fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
        fontSize: 12,
        background: "var(--lilia-bg-muted)",
      }}>
        <span style={{ color: "var(--lilia-text-muted)" }}>\</span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="command, name, …"
          style={{
            flex: 1, border: 0, outline: "none", background: "transparent",
            fontFamily: "inherit", fontSize: 12,
            color: "var(--lilia-text)",
          }}
        />
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: "0 6px", border: 0, background: "transparent",
            color: "var(--lilia-text-muted)", cursor: "pointer",
            fontSize: 14, lineHeight: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        {results.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12, color: "var(--lilia-text-muted)", textAlign: "center" }}>
            No commands match "<strong>{query}</strong>".
          </div>
        ) : (
          results.map((item, i) => {
            const isSel = i === selIdx;
            return (
              <button
                key={`${item.cat}-${item.entry.c}`}
                type="button"
                onMouseEnter={() => setSelIdx(i)}
                onClick={() => commit(item)}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 10px",
                  border: 0, background: isSel ? item.col.soft : "transparent",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 26, height: 26, borderRadius: 4,
                    background: item.col.soft, color: item.col.ink,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--lilia-font-serif, Georgia, serif)",
                    fontSize: 16,
                  }}
                >
                  {item.entry.g}
                </span>
                <span style={{
                  flex: 1, display: "flex", flexDirection: "column", gap: 1,
                  minWidth: 0,
                }}>
                  <span style={{
                    fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
                    fontSize: 12, color: "var(--lilia-text)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{item.entry.c}</span>
                  <span style={{
                    fontSize: 10, color: "var(--lilia-text-muted)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{item.entry.n} · {item.col.name}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
